/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LANDING } from "@/constants/testIds";
import { submitLead } from "@/lib/api";
import { getLeadSource } from "@/lib/leadSource";

const INITIAL = { name: "", email: "", company: "", phone: "", quantity: "", message: "" };

const COPY = {
  meeting: {
    eyebrow: "Book a Meeting",
    title: (
      <>
        Let's talk <em>gifting.</em>
      </>
    ),
    sub: "Share a few details and our team will reach out within one business day to schedule a call.",
    submit: "Request a Meeting",
    toast: "Thanks — we'll reach out shortly to schedule your meeting.",
  },
  bulk: {
    eyebrow: "Bulk Order Enquiry",
    title: (
      <>
        Corporate <em>gifting, at scale.</em>
      </>
    ),
    sub: "Tell us about your occasion and quantity. We'll craft a proposal tailored to your team.",
    submit: "Send Enquiry",
    toast: "Enquiry received — our bulk team will be in touch soon.",
  },
  advisor: {
    eyebrow: "Speak to a Gift Advisor",
    title: (
      <>
        Need help choosing <em>Diwali gifts?</em> Let us help you.
      </>
    ),
    sub: "Connect with a Gift Advisor for personalised gifting options based on your requirements, budget and quantity — and get a quote within 10 minutes.",
    submit: "Submit Form",
    toast: "Thanks — a Gift Advisor will reach out shortly.",
  },
};

// Every Utsah form asks for the same six fields — the last two are optional.
const FIELD_ORDER = ["name", "email", "phone", "company", "quantity", "message"];

const LABELS = {
  name: "Your Name",
  email: "Work Email Address",
  phone: "Phone Number",
  company: "Company Name",
  quantity: "Quantity Required",
  message: "Remarks",
};

const TESTIDS = {
  name: LANDING.leadName,
  email: LANDING.leadEmail,
  company: LANDING.leadCompany,
  phone: LANDING.leadPhone,
  quantity: LANDING.leadQuantity,
  message: LANDING.leadMessage,
};

const FULL_WIDTH = new Set(["message"]);

export default function LeadDialog({ open, type = "meeting", source, onOpenChange }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const copy = COPY[type] || COPY.meeting;

  // Reset form each time the dialog is opened
  useEffect(() => {
    if (open) setForm(INITIAL);
  }, [open, type]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [];
    if (!form.name.trim()) missing.push("name");
    if (!form.email.trim()) missing.push("email");
    if (!form.phone.trim()) missing.push("phone");
    if (!form.company.trim()) missing.push("company");
    if (missing.length) {
      toast.error(`Please fill in your ${missing.join(", ")}.`);
      return;
    }
    setSubmitting(true);
    try {
      // Backend accepts lead_type "meeting" | "bulk" only. The `advisor` variant
      // is a UI-only copy override and is recorded as a regular meeting lead so
      // it flows through the exact same enquiry-submission pipeline used
      // everywhere else on the site (validated by the same backend, saved to
      // the same collection, visible in the same /admin/leads dashboard).
      const backendLeadType = type === "advisor" ? "meeting" : type;
      const payload = { lead_type: backendLeadType, source: getLeadSource(source) };
      FIELD_ORDER.forEach((k) => {
        const v = form[k]?.trim();
        if (v) payload[k] = v;
      });
      await submitLead(payload);
      onOpenChange?.(false);
      navigate("/thank-you");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (typeof detail === "string" ? detail : null);
      toast.error(msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="lead-dialog-content"
        data-testid={LANDING.leadDialog}
      >
        <div className="lead-dialog-eyebrow">{copy.eyebrow}</div>
        <DialogTitle
          className="lead-dialog-title"
          data-testid={LANDING.leadDialogTitle}
        >
          {copy.title}
        </DialogTitle>
        <DialogDescription className="lead-dialog-sub">
          {copy.sub}
        </DialogDescription>

        <form className="lead-form" onSubmit={handleSubmit} noValidate>
          {FIELD_ORDER.map((k) => (
            <div key={k} className={`lead-field ${FULL_WIDTH.has(k) ? "full" : ""}`.trim()}>
              <label htmlFor={`lead-${k}`}>{LABELS[k]}</label>
              {k === "message" ? (
                <textarea
                  id={`lead-${k}`}
                  className="lead-textarea"
                  data-testid={TESTIDS[k]}
                  value={form[k]}
                  onChange={update(k)}
                  rows={4}
                  placeholder={
                    type === "bulk"
                      ? "Occasion, timeline, custom requirements..."
                      : "Anything you'd like us to know?"
                  }
                />
              ) : (
                <input
                  id={`lead-${k}`}
                  className="lead-input"
                  data-testid={TESTIDS[k]}
                  value={form[k]}
                  onChange={update(k)}
                  type={k === "email" ? "email" : k === "phone" ? "tel" : "text"}
                  required={k === "name" || k === "email" || k === "phone" || k === "company"}
                  placeholder={
                    k === "email"
                      ? "you@company.com"
                      : k === "phone"
                      ? "+91 98765 43210"
                      : k === "quantity"
                      ? "e.g. 200 hampers"
                      : ""
                  }
                />
              )}
            </div>
          ))}
          <button
            type="submit"
            className="lead-submit"
            data-testid={LANDING.leadSubmit}
            disabled={submitting}
          >
            {submitting ? "Sending…" : copy.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
